import { cn } from '@/lib/utils';
import { kr } from '../../lib/money';
import type { RouterOutputs } from '../../lib/trpc';
import { Meter, Panel } from '../common/Panel';

type Forecast = RouterOutputs['insights']['monthForecast'];

const STATUS = {
  under: { label: 'Lugnare än vanligt', className: 'bg-positive-soft text-positive' },
  // biome-ignore lint/style/useNamingConvention: mirrors the API's status value
  on_track: { label: 'Som vanligt', className: 'bg-positive-soft text-positive' },
  over: { label: 'Mer än vanligt', className: 'bg-warning-soft text-warning' },
} as const;

// Labels wrap to two lines on phones; reserve both so the figures line up.
const STAT_LABEL =
  'flex min-h-[2.5em] items-end justify-center text-[11px] leading-tight text-muted-foreground sm:block sm:min-h-0';

/** Are we spending faster than usual? Period-to-date vs last period and the average. */
export function PaceCard({ forecast }: { forecast: Forecast }) {
  const { period, current, pace, averages } = forecast;

  if (!period.isPast && !period.isCurrent) {
    return (
      <Panel title="Takt">
        <p className="text-sm text-muted-foreground">
          Perioden har inte börjat än. Prognosen bygger på snittet av tidigare perioder:{' '}
          <span className="font-medium text-foreground tabular-nums">{kr(averages.spending)}</span>{' '}
          i utgifter.
        </p>
      </Panel>
    );
  }

  const status = STATUS[pace.status];
  const dayShare = period.daysInPeriod > 0 ? period.daysElapsed / period.daysInPeriod : 1;

  return (
    <section className="rounded-2xl border border-border bg-card p-4 sm:p-5">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-muted-foreground">
          {period.isPast ? 'Utgifter under perioden' : 'Utgifter hittills'}
        </p>
        {period.isCurrent && (
          <span
            className={cn('rounded-full px-2.5 py-0.5 text-xs font-semibold', status.className)}
          >
            {status.label}
          </span>
        )}
      </div>

      <p className="mt-1 text-4xl leading-none font-semibold tracking-tight tabular-nums">
        {kr(current.spending)}
      </p>

      {period.isCurrent && (
        <div className="mt-4 space-y-1.5">
          <Meter
            value={dayShare}
            label={`Dag ${period.daysElapsed} av ${period.daysInPeriod} i perioden`}
          />
          <p className="text-xs text-muted-foreground tabular-nums">
            Dag {period.daysElapsed} av {period.daysInPeriod}
          </p>
        </div>
      )}

      <div className="mt-4 grid grid-cols-3 divide-x divide-border rounded-xl bg-muted/60 py-2.5 text-center">
        <PaceStat
          label={period.isPast ? 'Mot förra' : 'Mot förra, samma dag'}
          pct={pace.vsLastPeriodPct}
          base={pace.lastPeriodSameDay}
        />
        <PaceStat
          label={period.isPast ? 'Mot snittet' : 'Mot snittet, samma dag'}
          pct={pace.vsAvgPct}
          base={pace.avgSameDay}
        />
        <span className="px-1">
          <span className={STAT_LABEL}>{period.isPast ? 'Snitt tidigare' : 'Prognos'}</span>
          <span className="block text-sm font-semibold tabular-nums">
            {kr(period.isPast ? averages.spending : current.forecastSpending)}
          </span>
        </span>
      </div>

      {period.isCurrent && averages.spending > 0 && (
        <p className="mt-2 text-xs text-muted-foreground">
          Prognosen för hela perioden. En vanlig period landar på{' '}
          <span className="tabular-nums">{kr(averages.spending)}</span>.
        </p>
      )}
    </section>
  );
}

function PaceStat({ label, pct, base }: { label: string; pct: number; base: number }) {
  const neutral = base === 0;
  return (
    <span className="px-1">
      <span className={STAT_LABEL}>{label}</span>
      <span
        className={cn(
          'block text-sm font-semibold tabular-nums',
          !neutral && (pct > 0 ? 'text-warning' : 'text-positive'),
        )}
      >
        {neutral ? '–' : `${pct > 0 ? '+' : ''}${pct} %`}
      </span>
      {!neutral && (
        <span className="block text-[11px] text-muted-foreground tabular-nums">{kr(base)} då</span>
      )}
    </span>
  );
}
